import clsx from 'clsx';
import { Icons } from 'copyright-clearance-center-common-ui';
import moment from 'moment';
import React, { MutableRefObject, useEffect, useState } from 'react';

export interface IDatePickerInputProps {
	inputWrapperRef: MutableRefObject<HTMLDivElement>;
	value: Date | null;
	format?: string;
	placeholder?: string;
	testId?: string;
	error?: string;
	disabled?: boolean;
	onChange: (newDate: Date | undefined) => void;
	toggleCalendar: (open?: boolean) => void;
	refreshSignal: number;
	preserveInvalidValue: boolean;
}

const getDateFormatMaxLength = (dateFormat: string): number => {
	// dateFormat.length + 2 to support MM and DD, when short date pattern is YYYY/M/D
	if (dateFormat.toLocaleLowerCase().includes('d')) {
		return dateFormat.length + 2;
	}

	// dateFormat.length + 1 to support MM, when short date pattern is YYYY/M
	return dateFormat.length + 1;
};

const DatePickerInput: React.FunctionComponent<IDatePickerInputProps> = ({
	onChange,
	toggleCalendar,
	testId,
	value,
	format,
	placeholder,
	error,
	disabled,
	inputWrapperRef,
	refreshSignal,
	preserveInvalidValue
}) => {
	const [displayValue, setDisplayValue] = useState('');
	const [dateFormat, setDateFormat] = useState<string>();
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => setDateFormat(format?.toUpperCase() ?? 'MM/DD/YYYY'), [format]);
	useEffect(() => {
		if (!preserveInvalidValue || !displayValue || !value || !isNaN(value?.valueOf())) {
			setDisplayValue(value ? moment(value).format(dateFormat) : '');
		}
	}, [value, dateFormat, refreshSignal]);

	return (
		<div
			ref={inputWrapperRef}
			className={clsx('rup-input input-wrapper', {
				['rup-input--disabled']: disabled,
				['rup-input--error']: error,
				['rup-input--focus']: isFocused
			})}
			style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
		>
			<input
				data-test-id={testId}
				className="input"
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				disabled={disabled}
				value={displayValue}
				onClick={() => toggleCalendar(true)}
				placeholder={placeholder || dateFormat}
				maxLength={dateFormat ? getDateFormatMaxLength(dateFormat) : 0}
				onChange={e => {
					const currValue = e.target.value;
					const dateMoment = moment(currValue, dateFormat);
					setDisplayValue(currValue);
					if (currValue && (dateMoment.isValid() || preserveInvalidValue)) {
						const newDate = dateMoment.toDate();
						onChange(newDate);
					} else {
						onChange(undefined);
					}
				}}
				style={{ cursor: disabled ? 'not-allowed' : 'text' }}
			/>
			<span
				onClick={() => !disabled && toggleCalendar()}
				className="rup-calendar__icon rup-icon rup-icon--sm"
				dangerouslySetInnerHTML={{ __html: Icons.CalendarIcon }}
				style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
			></span>
		</div>
	);
};

export default DatePickerInput;
